import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useAuth } from "../../AuthContext";
import api from "../../api";

function Contact() {
  const { user } = useAuth();

  const [formData, setFormData] = useState({
    firstname: "",
    lastname: "",
    email: "",
    message: "",
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (user) {
      setFormData((prev) => ({
        ...prev,
        firstname: user.firstname ?? "",
        lastname: user.lastname ?? "",
        email: user.email ?? "",
      }));
    }
  }, [user]);

  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    if (error) {
      setError("");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (loading) return;

    try {
      setLoading(true);
      setError("");

      await api.post("/contact", {
        firstname: formData.firstname,
        lastname: formData.lastname,
        email: formData.email,
        message: formData.message,
      });

      toast.success("Message envoyé avec succès !", {
        position: "top-right",
        autoClose: 4000,
      });

      setFormData((prev) => ({
        ...prev,
        message: "",
      }));
    } catch (err) {
      console.error(err);

      if (err.response?.data?.errors) {
        const msgs = Object.values(err.response.data.errors).flat().join(" ");
        setError(msgs);
      } else {
        setError(
          err.response?.data?.message ||
            "Erreur lors de l'envoi du message. Veuillez réessayer."
        );
      }

      toast.error("Erreur lors de l'envoi du message.", {
        position: "top-right",
        autoClose: 4000,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setFormData({
      firstname: user?.firstname ?? "",
      lastname: user?.lastname ?? "",
      email: user?.email ?? "",
      message: "",
    });

    setError("");
  };

  const isDisabled =
    loading ||
    !formData.firstname.trim() ||
    !formData.lastname.trim() ||
    !formData.email.trim() ||
    !formData.message.trim();

  const pageTitle = "Contact - LMFP Photography";
  const pageDescription =
    "Contactez LMFP Photography pour un projet photo, une collaboration ou toute demande d'information.";

  return (
    <>
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={pageDescription} />
        <meta name="robots" content="noindex, nofollow" />
        <link rel="canonical" href="https://www.lmfp-photography.com/contact" />

        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDescription} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://www.lmfp-photography.com/contact" />
        <meta property="og:site_name" content="LMFP Photography" />
        <meta property="og:locale" content="fr_FR" />

        <meta name="twitter:card" content="summary" />
        <meta name="twitter:title" content={pageTitle} />
        <meta name="twitter:description" content={pageDescription} />
      </Helmet>

      <div className="min-h-screen bg-slate-950 px-4 py-10 text-slate-100">
        <div className="mx-auto max-w-4xl">
          <section className="mb-8 overflow-hidden rounded-3xl bg-gradient-to-r from-slate-900 via-slate-800 to-blue-900 text-white shadow-sm">
            <div className="p-8 md:p-10">
              <p className="mb-3 inline-flex rounded-full bg-white/10 px-3 py-1 text-sm font-medium text-blue-100">
                Contact
              </p>

              <h1 className="mb-3 text-3xl font-bold md:text-4xl">
                Envoyer un message
              </h1>

              <p className="max-w-2xl text-sm leading-7 text-slate-200 md:text-base">
                Une question, une collaboration ou un projet photo en tête ?
                Utilisez ce formulaire pour entrer en contact.
              </p>
            </div>
          </section>

          <section className="rounded-2xl border border-slate-800 bg-slate-900 p-6 shadow-sm">
            <div className="mb-6 rounded-2xl border border-slate-800 bg-slate-950 p-5 text-sm leading-6 text-slate-300">
              💬 Vos retours et suggestions sur le site sont également les
              bienvenus via ce formulaire.
            </div>

            {error && (
              <div className="mb-6 rounded-xl border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-300">
                {error}
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-8">
              <div className="rounded-2xl border border-slate-800 bg-slate-950 p-6">
                <h2 className="mb-5 text-lg font-semibold text-white">
                  Vos informations
                </h2>

                <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                  <div>
                    <label
                      htmlFor="firstname"
                      className="mb-2 block text-sm font-medium text-slate-200"
                    >
                      Prénom
                    </label>
                    <input
                      id="firstname"
                      name="firstname"
                      type="text"
                      value={formData.firstname}
                      onChange={handleChange}
                      required
                      placeholder="Votre prénom"
                      className="block w-full rounded-xl border border-slate-700 bg-slate-900 px-4 py-3 text-slate-100 placeholder:text-slate-500 focus:border-blue-500 focus:outline-none"
                    />
                  </div>

                  <div>
                    <label
                      htmlFor="lastname"
                      className="mb-2 block text-sm font-medium text-slate-200"
                    >
                      Nom
                    </label>
                    <input
                      id="lastname"
                      name="lastname"
                      type="text"
                      value={formData.lastname}
                      onChange={handleChange}
                      required
                      placeholder="Votre nom"
                      className="block w-full rounded-xl border border-slate-700 bg-slate-900 px-4 py-3 text-slate-100 placeholder:text-slate-500 focus:border-blue-500 focus:outline-none"
                    />
                  </div>

                  <div className="md:col-span-2">
                    <label
                      htmlFor="email"
                      className="mb-2 block text-sm font-medium text-slate-200"
                    >
                      Email
                    </label>
                    <input
                      id="email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      placeholder="votre@email.com"
                      className="block w-full rounded-xl border border-slate-700 bg-slate-900 px-4 py-3 text-slate-100 placeholder:text-slate-500 focus:border-blue-500 focus:outline-none"
                    />
                  </div>
                </div>
              </div>

              <div className="rounded-2xl border border-slate-800 bg-slate-950 p-6">
                <h2 className="mb-5 text-lg font-semibold text-white">
                  Votre message
                </h2>

                <div>
                  <label
                    htmlFor="message"
                    className="mb-2 block text-sm font-medium text-slate-200"
                  >
                    Message
                  </label>

                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows="7"
                    placeholder="Votre message"
                    className="block w-full resize-none rounded-xl border border-slate-700 bg-slate-900 px-4 py-3 text-slate-100 placeholder:text-slate-500 focus:border-blue-500 focus:outline-none"
                  />

                  <div className="mt-3 flex flex-col gap-2 text-sm text-slate-400 sm:flex-row sm:items-center sm:justify-between">
                    <p>Je reviens vers vous le plus vite possible.</p>
                    <span>{formData.message.length} caractères</span>
                  </div>
                </div>
              </div>

              <div className="flex flex-col gap-3 sm:flex-row">
                <button
                  type="submit"
                  disabled={isDisabled}
                  className="w-full rounded-xl bg-blue-600 px-6 py-3 font-semibold text-white transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto"
                >
                  {loading ? "Envoi..." : "Envoyer"}
                </button>

                <button
                  type="button"
                  onClick={handleReset}
                  disabled={loading}
                  className="w-full rounded-xl border border-slate-700 px-6 py-3 font-semibold text-slate-200 transition hover:bg-slate-800 disabled:opacity-60 sm:w-auto"
                >
                  Réinitialiser
                </button>
              </div>
            </form>
          </section>

          <ToastContainer />
        </div>
      </div>
    </>
  );
}

export default Contact;